import { useEffect, useState } from 'react';
import { axiosInstance } from '../../App';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { SignatureCanvas } from '../SignatureCanvas';
import { ArrowLeft, Save, FileDown, Mail, Camera, Trash2, PenLine, Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { downloadReportPdf } from './downloadReportPdf';

const FIELDS = ['title', 'client_name', 'client_email', 'client_phone', 'client_address', 'client_city', 'invoice_id', 'invoice_number', 'report_date', 'damage_date', 'location_in_building', 'description', 'cause', 'notes', 'status'];
const pick = (r) => Object.fromEntries(FIELDS.map(k => [k, r[k] ?? '']));

export const DamageReportEditor = ({ report, user, onBack, onChange }) => {
  const [form, setForm] = useState(pick(report));
  const [invoices, setInvoices] = useState([]);
  const [invoiceQ, setInvoiceQ] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const [sig, setSig] = useState('');
  const [sendOpen, setSendOpen] = useState(false);
  const [sendTo, setSendTo] = useState('');
  const [sendMsg, setSendMsg] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axiosInstance.get('/invoices').then(r => setInvoices(r.data || [])).catch(() => {});
    axiosInstance.get(`/schade-rapporten/${report.id}`).then(r => onChange(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async (silent = false) => {
    setSaving(true);
    try {
      const res = await axiosInstance.put(`/schade-rapporten/${report.id}`, form);
      onChange(res.data);
      if (!silent) toast.success('Schaderapport opgeslagen');
      return res.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Opslaan mislukt');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const pickInvoice = (id) => {
    const inv = invoices.find(i => i.id === id);
    if (!inv) return;
    setForm(f => ({
      ...f,
      invoice_id: inv.id,
      invoice_number: inv.number || '',
      client_name: f.client_name || inv.client_name || '',
      client_email: f.client_email || inv.client_email || '',
      client_phone: f.client_phone || inv.client_phone || '',
      client_address: f.client_address || inv.client_address || '',
      client_city: f.client_city || inv.client_city || '',
      title: f.title || inv.description || '',
    }));
    toast.success(`Factuur ${inv.number} gekoppeld — klantgegevens overgenomen`);
  };

  const uploadPhotos = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const fd = new FormData();
    Array.from(files).forEach(f => fd.append('files', f));
    try {
      const res = await axiosInstance.post(`/schade-rapporten/${report.id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange({ ...report, photos: res.data.photos });
      toast.success(`${res.data.added.length} foto's toegevoegd`);
    } catch (e) {
      toast.error('Foto upload mislukt');
    } finally {
      setUploading(false);
    }
  };

  const deletePhoto = async (p) => {
    try {
      await axiosInstance.delete(`/schade-rapporten/${report.id}/photos/${p.id}`);
      onChange({ ...report, photos: (report.photos || []).filter(x => x.id !== p.id) });
    } catch (e) {
      toast.error('Foto verwijderen mislukt');
    }
  };

  const saveSignature = async () => {
    if (!sig) return toast.error('Teken eerst je handtekening');
    try {
      await axiosInstance.put(`/schade-rapporten/${report.id}/signature`, { signature: sig, signer_name: user.name });
      onChange({ ...report, signature: sig, signer_name: user.name, signed_at: new Date().toISOString() });
      setSigOpen(false);
      toast.success('Handtekening opgeslagen');
    } catch (e) {
      toast.error('Handtekening opslaan mislukt');
    }
  };

  const openSend = async () => {
    const saved = await save(true);
    if (!saved) return;
    setSendTo(form.client_email || '');
    setSendOpen(true);
  };

  const send = async () => {
    const to = sendTo.split(/[,;\s]+/).filter(Boolean);
    if (!to.length) return toast.error('Vul een e-mailadres in');
    setSending(true);
    try {
      const res = await axiosInstance.post(`/schade-rapporten/${report.id}/send`, { to, message: sendMsg });
      toast.success(res.data.message);
      onChange({ ...report, ...form, status: 'verzonden', sent_at: res.data.sent_at });
      setForm(f => ({ ...f, status: 'verzonden' }));
      setSendOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Versturen mislukt');
    } finally {
      setSending(false);
    }
  };

  const pdf = async () => {
    const saved = await save(true);
    if (saved) downloadReportPdf(saved);
  };

  const iq = invoiceQ.trim().toLowerCase();
  const invoiceOptions = (iq ? invoices.filter(i => [i.number, i.client_name, i.client_address, i.description].some(v => (v || '').toLowerCase().includes(iq))) : invoices).slice(0, 60);
  const photos = report.photos || [];

  return (
    <div className="space-y-6" data-testid="damage-report-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={onBack} data-testid="damage-report-back"><ArrowLeft className="w-4 h-4 mr-1" />Terug</Button>
          <h1 className="text-3xl font-bold text-gray-800">Schaderapport <span className="text-[#3da5a5]">{report.number}</span></h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => save()} disabled={saving} className="bg-[#3da5a5] hover:bg-[#2d8585] text-white" data-testid="damage-report-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Opslaan
          </Button>
          <Button variant="outline" onClick={pdf} data-testid="damage-report-pdf"><FileDown className="w-4 h-4 mr-1" />PDF downloaden</Button>
          <Button onClick={openSend} className="bg-[#1a5555] hover:bg-[#0d3d3d] text-white" data-testid="damage-report-send"><Mail className="w-4 h-4 mr-1" />Versturen per e-mail</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
        <div className="space-y-6">
          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-3"><CardTitle className="text-lg flex items-center gap-2"><FileText className="w-5 h-5 text-[#3da5a5]" />Factuur koppelen (optioneel)</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Input value={invoiceQ} onChange={e => setInvoiceQ(e.target.value)} placeholder="Zoek factuur op nummer, klant of adres…" className="bg-gray-50" data-testid="damage-invoice-search" />
              <Select value={form.invoice_id || ''} onValueChange={pickInvoice}>
                <SelectTrigger className="bg-gray-50" data-testid="damage-invoice-select"><SelectValue placeholder="Kies een factuur — klantgegevens worden overgenomen" /></SelectTrigger>
                <SelectContent>
                  {invoiceOptions.map(i => (
                    <SelectItem key={i.id} value={i.id}>{i.number} — {i.client_name}{i.client_address ? ` · ${i.client_address}` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.invoice_number && <p className="text-sm text-gray-600">Gekoppeld: <b>{form.invoice_number}</b> <button className="text-xs underline text-gray-400 ml-2" onClick={() => setForm(f => ({ ...f, invoice_id: '', invoice_number: '' }))}>loskoppelen</button></p>}
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-3"><CardTitle className="text-lg">Klant &amp; schadelocatie</CardTitle></CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><Label>Titel / onderwerp</Label><Input value={form.title} onChange={e => set('title', e.target.value)} placeholder="bijv. Waterschade badkamer" data-testid="damage-title" /></div>
              <div><Label>Naam klant *</Label><Input value={form.client_name} onChange={e => set('client_name', e.target.value)} data-testid="damage-client-name" /></div>
              <div><Label>E-mail klant</Label><Input type="email" value={form.client_email} onChange={e => set('client_email', e.target.value)} data-testid="damage-client-email" /></div>
              <div><Label>Adres *</Label><Input value={form.client_address} onChange={e => set('client_address', e.target.value)} data-testid="damage-client-address" /></div>
              <div><Label>Postcode / plaats</Label><Input value={form.client_city} onChange={e => set('client_city', e.target.value)} data-testid="damage-client-city" /></div>
              <div><Label>Telefoon</Label><Input value={form.client_phone} onChange={e => set('client_phone', e.target.value)} /></div>
              <div><Label>Locatie in het pand</Label><Input value={form.location_in_building} onChange={e => set('location_in_building', e.target.value)} placeholder="bijv. Badkamer 1e verdieping" data-testid="damage-location" /></div>
              <div><Label>Datum constatering *</Label><Input type="date" value={form.damage_date} onChange={e => set('damage_date', e.target.value)} data-testid="damage-date" /></div>
              <div><Label>Rapportdatum</Label><Input type="date" value={form.report_date} onChange={e => set('report_date', e.target.value)} data-testid="damage-report-date" /></div>
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-3"><CardTitle className="text-lg">Schade</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><Label>Omschrijving van de schade *</Label><Textarea rows={5} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Wat is er beschadigd, omvang, materialen…" data-testid="damage-description" /></div>
              <div><Label>Oorzaak van de schade *</Label><Textarea rows={5} value={form.cause} onChange={e => set('cause', e.target.value)} placeholder="Jouw vaststelling van de oorzaak…" data-testid="damage-cause" /></div>
              <div><Label>Aanvullende opmerkingen</Label><Textarea rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} data-testid="damage-notes" /></div>
              <div className="max-w-xs"><Label>Status</Label>
                <Select value={form.status || 'concept'} onValueChange={v => set('status', v)}>
                  <SelectTrigger data-testid="damage-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="concept">Concept</SelectItem><SelectItem value="definitief">Definitief</SelectItem><SelectItem value="verzonden">Verzonden</SelectItem></SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-3"><CardTitle className="text-lg flex items-center gap-2"><Camera className="w-5 h-5 text-[#3da5a5]" />Foto's ({photos.length})</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#3da5a5]/50 rounded-xl p-6 cursor-pointer hover:bg-[#3da5a5]/5 transition-colors">
                {uploading ? <Loader2 className="w-6 h-6 animate-spin text-[#3da5a5]" /> : <Camera className="w-6 h-6 text-[#3da5a5]" />}
                <span className="text-sm text-gray-600">{uploading ? 'Uploaden…' : 'Foto\'s kiezen of maken'}</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={e => { uploadPhotos(e.target.files); e.target.value = ''; }} data-testid="damage-photo-input" />
              </label>
              {photos.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="damage-photo-grid">
                  {photos.map(p => (
                    <div key={p.id} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200">
                      <img src={p.url} alt={p.filename} className="w-full h-full object-cover" />
                      <button onClick={() => deletePhoto(p)} className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-photo-${p.id}`}><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-3"><CardTitle className="text-lg flex items-center gap-2"><PenLine className="w-5 h-5 text-[#3da5a5]" />Handtekening</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {report.signature ? (
                <div data-testid="damage-signature-preview">
                  <img src={report.signature} alt="Handtekening" className="h-24 border border-gray-200 rounded bg-white" />
                  <p className="text-xs text-gray-500 mt-1">{report.signer_name} · {report.signed_at ? new Date(report.signed_at).toLocaleDateString('nl-NL') : ''}</p>
                </div>
              ) : <p className="text-sm text-gray-500">Nog niet ondertekend.</p>}
              <Button variant="outline" onClick={() => { setSig(''); setSigOpen(true); }} data-testid="damage-sign-btn"><PenLine className="w-4 h-4 mr-1" />{report.signature ? 'Opnieuw tekenen' : 'Ondertekenen'}</Button>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={sigOpen} onOpenChange={setSigOpen}>
        <DialogContent className="bg-white max-w-lg" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Handtekening {user.name}</DialogTitle></DialogHeader>
          <SignatureCanvas onChange={setSig} testId="damage-signature-canvas" />
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setSigOpen(false)}>Annuleren</Button><Button onClick={saveSignature} className="bg-[#3da5a5] text-white" data-testid="damage-signature-save">Opslaan</Button></div>
        </DialogContent>
      </Dialog>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="bg-white max-w-lg" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Schaderapport {report.number} versturen</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Aan (meerdere adressen scheiden met komma)</Label><Input value={sendTo} onChange={e => setSendTo(e.target.value)} placeholder="klant@voorbeeld.nl, verzekeraar@voorbeeld.nl" data-testid="damage-send-to" /></div>
            <div><Label>Bericht (optioneel)</Label><Textarea rows={4} value={sendMsg} onChange={e => setSendMsg(e.target.value)} data-testid="damage-send-message" /></div>
            <p className="text-xs text-gray-500">De PDF met foto's en handtekening wordt als bijlage meegestuurd.</p>
          </div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setSendOpen(false)}>Annuleren</Button>
            <Button onClick={send} disabled={sending} className="bg-[#1a5555] text-white" data-testid="damage-send-confirm">{sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}Versturen</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
